/**
 * Custom Sticky Cart – Mamaearth floating "View cart" pill.
 * Syncs with Shopify cart only (no GoKwik). Opens native cart drawer + fireViewCart.
 *
 * Performance: updates as soon as add.js / change.js / clear.js succeed
 * (optimistic merge), instead of waiting for the follow-up cart.js + drawer events.
 */
(function () {
  "use strict";

  const nativeFetch = window.fetch.bind(window);
  const fetchCart = () => nativeFetch("/cart.js").then((r) => r.json());

  const excludedPaths = [
    "/cart",
    "/checkout",
    "/pages/orders",
    "/pages/skin-assessment",
    "/pages/my-assessments",
    "/pages/refer",
    "/pages/wallet",
    "/pages/educate",
  ];

  const isCustomersPage = () =>
    Array.from(document.body.classList || []).some(
      (c) =>
        c.startsWith("hdt-page-type-customers") ||
        c.startsWith("template-customers"),
    );

  const isExcludedPage = () => {
    const path = typeof location !== "undefined" ? location.pathname : "";
    if (isCustomersPage()) return true;
    if (document.body.classList.contains("template-cart")) return true;
    return excludedPaths.some((p) => path === p || path.indexOf(p + "/") === 0);
  };

  const container = () => document.getElementById("custom-sticky-cart");
  const imagesEl = () =>
    document.querySelector("[data-custom-sticky-cart-images]");
  const countEl = () =>
    document.querySelector("[data-custom-sticky-cart-count]");
  const trigger = () =>
    document.querySelector("[data-custom-sticky-cart-trigger]");

  let lastItemCount = -1;
  let lastLineCount = 0;
  let lastKnownCart = null;
  let lastSignature = "";
  /** Line key that must stay visually in front (most recently added / bumped). */
  let lastFrontKey = null;
  /** Previous front key — used as the second (behind) thumbnail. */
  let prevFrontKey = null;

  function lineImage(item) {
    if (!item) return "";
    var src = "";
    if (typeof item.image === "string") src = item.image;
    else if (item.featured_image) {
      src = item.featured_image.url || item.featured_image.src || "";
    }
    if (!src) return "";
    return src.indexOf("//") === 0 ? "https:" + src : src;
  }

  function lineKey(item) {
    if (!item) return "";
    return item.key || String(item.variant_id || item.id || "");
  }

  function rememberFrontKey(key) {
    if (!key) return;
    if (key !== lastFrontKey) {
      if (lastFrontKey) prevFrontKey = lastFrontKey;
      lastFrontKey = key;
    }
  }

  /**
   * Newest thumbnail first (front), one older behind — independent of cart.items order.
   * Cart.js may list oldest-first; optimistic merge may differ; without pinning, the
   * latest image flashes in front then swaps behind on reconcile.
   */
  function getDisplayItems(cart) {
    var withImg = (cart.items || []).filter(function (i) {
      return !!lineImage(i);
    });
    if (!withImg.length) return [];
    if (withImg.length === 1) {
      rememberFrontKey(lineKey(withImg[0]));
      return withImg;
    }

    var newestIdx = -1;
    if (lastFrontKey) {
      newestIdx = withImg.findIndex(function (i) {
        return lineKey(i) === lastFrontKey;
      });
    }
    if (newestIdx < 0) {
      // Shopify Ajax cart: most recently added line is last
      newestIdx = withImg.length - 1;
    }

    var newest = withImg[newestIdx];
    var second = null;
    if (prevFrontKey && prevFrontKey !== lineKey(newest)) {
      second =
        withImg.find(function (i) {
          return lineKey(i) === prevFrontKey;
        }) || null;
    }
    if (!second) {
      for (var i = withImg.length - 1; i >= 0; i--) {
        if (i === newestIdx) continue;
        second = withImg[i];
        break;
      }
    }

    rememberFrontKey(lineKey(newest));
    return second ? [newest, second] : [newest];
  }

  function detectFrontKeyFromCartDelta(prev, next) {
    if (!next || !Array.isArray(next.items)) return null;
    if (!prev || !Array.isArray(prev.items) || !prev.items.length) {
      return next.items.length
        ? lineKey(next.items[next.items.length - 1])
        : null;
    }

    for (var i = 0; i < next.items.length; i++) {
      var n = next.items[i];
      var nKey = lineKey(n);
      var p = prev.items.find(function (x) {
        return lineKey(x) === nKey;
      });
      if (!p) return nKey;
      if ((parseInt(n.quantity, 10) || 0) > (parseInt(p.quantity, 10) || 0)) {
        return nKey;
      }
    }

    if (
      lastFrontKey &&
      next.items.some(function (i) {
        return lineKey(i) === lastFrontKey;
      })
    ) {
      return lastFrontKey;
    }
    return next.items.length
      ? lineKey(next.items[next.items.length - 1])
      : null;
  }

  /**
   * True only when a cart line was fully removed (qty → 0).
   * Qty-only decreases (3 → 2) keep the same items.length — never pop-out.
   * Do not compare line `key`s: optimistic add.js vs change.js/cart.js can use
   * different key shapes and falsely look like a removal.
   */
  function hasFullyRemovedLine(prev, next) {
    if (!prev || !Array.isArray(prev.items) || !prev.items.length) return false;
    if (!next || !Array.isArray(next.items)) return true;
    if (next.items.length >= prev.items.length) return false;

    // Extra guard: at least one variant id must be gone
    var nextVariants = {};
    next.items.forEach(function (item) {
      var id = String(item.variant_id || item.id || "");
      if (id) nextVariants[id] = true;
    });
    return prev.items.some(function (item) {
      var id = String(item.variant_id || item.id || "");
      return id && !nextVariants[id];
    });
  }

  function cartSignature(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
      return "empty:" + (cart && cart.item_count ? cart.item_count : 0);
    }
    // Order-independent so cart.js reorder alone does not thrash the DOM;
    // front key is appended separately in applyCart.
    var parts = cart.items
      .map(function (i) {
        return lineKey(i) + ":" + (i.quantity || 0);
      })
      .sort();
    return (cart.item_count || 0) + "|" + parts.join(",");
  }

  function normalizeAddLines(data) {
    if (!data || typeof data !== "object") return [];
    if (Array.isArray(data.items) && data.items.length) return data.items;
    if (data.id != null || data.variant_id != null || data.key) return [data];
    return [];
  }

  /** Build a cart-shaped object from add.js line(s) so UI need not wait for cart.js */
  function mergeAddIntoCart(prev, data) {
    var lines = normalizeAddLines(data);
    if (!lines.length) return prev;

    var cart = {
      item_count: prev && prev.item_count ? prev.item_count : 0,
      items:
        prev && Array.isArray(prev.items)
          ? prev.items.map(function (i) {
              return Object.assign({}, i);
            })
          : [],
    };

    lines.forEach(function (line) {
      var idx = cart.items.findIndex(function (i) {
        if (line.key && i.key) return i.key === line.key;
        return i.id === line.id || i.variant_id === line.variant_id;
      });
      var img = lineImage(line) || (idx >= 0 ? lineImage(cart.items[idx]) : "");
      var normalized = Object.assign({}, line, { image: img });

      if (idx >= 0) {
        var oldQty = parseInt(cart.items[idx].quantity, 10) || 0;
        var newQty = parseInt(normalized.quantity, 10) || oldQty;
        cart.item_count = Math.max(0, cart.item_count - oldQty + newQty);
        cart.items[idx] = Object.assign({}, cart.items[idx], normalized);
      } else {
        var qty = parseInt(normalized.quantity, 10) || 1;
        cart.item_count += qty;
        cart.items.push(normalized);
      }
      rememberFrontKey(lineKey(normalized));
    });

    return cart;
  }

  function requestUrl(input) {
    if (typeof input === "string") return input;
    if (input && typeof input.url === "string") return input.url;
    try {
      return String(input);
    } catch (e) {
      return "";
    }
  }

  function interceptCartMutationResponse(url, response) {
    if (!response || !response.ok) return;
    var path = url || "";
    var isAdd = /(?:^|\/)cart\/add\.js(?:\?|$)/.test(path);
    var isChange = /(?:^|\/)cart\/change\.js(?:\?|$)/.test(path);
    var isClear = /(?:^|\/)cart\/clear\.js(?:\?|$)/.test(path);
    if (!isAdd && !isChange && !isClear) return;

    response
      .clone()
      .json()
      .then(function (data) {
        if (isAdd) {
          var merged = mergeAddIntoCart(lastKnownCart, data);
          if (merged) applyCart(merged, { detectFront: false });
          return;
        }
        if (isClear) {
          lastFrontKey = null;
          prevFrontKey = null;
        } else if (data && typeof data === "object") {
          var bumped = detectFrontKeyFromCartDelta(lastKnownCart, data);
          if (bumped) rememberFrontKey(bumped);
        }
        // change.js / clear.js already return a full cart object
        if (data && typeof data === "object") {
          applyCart(data, { detectFront: false });
        }
      })
      .catch(function () {});
  }

  // Observe ATC / qty / clear as soon as Shopify returns — before theme's cart.js
  if (!window.__mamaStickyCartFetchPatched) {
    window.__mamaStickyCartFetchPatched = true;
    window.fetch = function (input, init) {
      var url = requestUrl(input);
      var method = (
        (init && init.method) ||
        (input && input.method) ||
        "GET"
      ).toUpperCase();
      var promise = nativeFetch(input, init);
      if (
        method === "POST" &&
        /(?:^|\/)cart\/(add|change|clear)\.js(?:\?|$)/.test(url)
      ) {
        promise.then(function (res) {
          interceptCartMutationResponse(url, res);
          return res;
        });
      }
      return promise;
    };
  }

  let positionTicking = false;
  const POSITION_GAP = 10;

  /** Height the pill must clear for the PDP sticky ATC (fixed bottom:0). */
  function stickyAtcClearance() {
    const stickyAtc = document.querySelector(".hdt-sticky-btn-atc");
    if (!stickyAtc || stickyAtc.offsetHeight <= 0) return 0;
    const alwaysOn = stickyAtc.classList.contains(
      "hdt-always-sticky-btn-atc-on-mobile",
    );
    if (!document.body.classList.contains("sticky-shown") && !alwaysOn) {
      return 0;
    }
    const atcBottom = parseFloat(getComputedStyle(stickyAtc).bottom);
    return (
      (Number.isFinite(atcBottom) ? atcBottom : 0) + stickyAtc.offsetHeight
    );
  }

  /**
   * Stack pill above progress bar + toolbar using CSS layout bottoms.
   * Avoid window.innerHeight - getBoundingClientRect(): on mobile scroll the
   * address bar / visual viewport shifts that math and the pill overlaps the bar.
   */
  function updatePosition() {
    const wrap = container();
    if (!wrap) return;

    const mq = window.matchMedia("(max-width: 767px)");
    if (!mq.matches) return;

    const progress = document.querySelector(".cart-progressBar");
    const toolbar =
      document.querySelector(".hdt-section-toolbar-mobile .hdt-toolbar") ||
      document.querySelector(".hdt-toolbar-mobile .hdt-toolbar");

    let bottom = 80;
    const progressStyle = progress ? getComputedStyle(progress) : null;
    const progressVisible =
      progress &&
      progressStyle &&
      progressStyle.display !== "none" &&
      progressStyle.visibility !== "hidden" &&
      progress.offsetHeight > 0 &&
      progress.getAttribute("aria-hidden") !== "true";

    if (progressVisible) {
      var progBottom = parseFloat(progressStyle.bottom);
      if (Number.isNaN(progBottom)) {
        // Fallback: CSS var used by the contractual strip
        var cssVar = getComputedStyle(document.documentElement)
          .getPropertyValue("--hdt-cart-progress-bottom")
          .trim();
        progBottom = parseFloat(cssVar);
      }
      if (Number.isNaN(progBottom)) progBottom = 72;
      bottom = Math.round(progBottom + progress.offsetHeight + POSITION_GAP);
    } else if (toolbar && toolbar.offsetHeight > 0) {
      const toolbarStyle = getComputedStyle(toolbar);
      var toolbarBottom = parseFloat(toolbarStyle.bottom);
      if (Number.isNaN(toolbarBottom)) toolbarBottom = 0;
      bottom = Math.round(
        toolbarBottom +
          toolbar.offsetHeight +
          (parseFloat(toolbarStyle.marginBottom) || 0) +
          POSITION_GAP,
      );
    }

    // Floor: never sit under the sticky ATC (Safari chrome collapse / missing strip)
    const atcClear = stickyAtcClearance();
    if (atcClear > 0) {
      bottom = Math.max(bottom, Math.round(atcClear + POSITION_GAP));
    }

    wrap.style.bottom = bottom + "px";
  }

  function schedulePositionUpdate() {
    if (positionTicking) return;
    positionTicking = true;
    requestAnimationFrame(function () {
      positionTicking = false;
      updatePosition();
    });
  }

  /**
   * Schedule a delayed position update to handle cases where the progress bar
   * is animating in or hasn't fully rendered yet. Used after initial cart add.
   */
  function scheduleDelayedPositionUpdate(delay) {
    delay = delay || 100;
    setTimeout(function () {
      schedulePositionUpdate();
    }, delay);
  }

  function rebuildImages(imgWrap, items, didAdd, isFirstItem) {
    imgWrap.innerHTML = "";
    items.forEach(function (item, index) {
      var src = lineImage(item);
      if (!src) return;
      var imgWrapEl = document.createElement("span");
      imgWrapEl.className =
        "custom-sticky-cart__img-wrap " +
        (index === 0
          ? "custom-sticky-cart__img-wrap--front"
          : "custom-sticky-cart__img-wrap--back");
      var img = document.createElement("img");
      img.src = src;
      img.alt = item.title || "";
      img.setAttribute("loading", "lazy");
      // Drop-in only when adding onto an already non-empty cart
      if (didAdd && index === 0 && !isFirstItem) {
        imgWrapEl.classList.add("custom-sticky-cart__img-wrap--drop-in");
        imgWrapEl.addEventListener(
          "animationend",
          function onDropEnd() {
            imgWrapEl.removeEventListener("animationend", onDropEnd);
            imgWrapEl.classList.remove("custom-sticky-cart__img-wrap--drop-in");
          },
          { once: true },
        );
      }
      imgWrapEl.appendChild(img);
      imgWrap.appendChild(imgWrapEl);
    });
  }

  function render(cart, opts) {
    const wrap = container();
    const imgWrap = imagesEl();
    const cnt = countEl();
    if (!wrap || !imgWrap || !cnt) return;
    opts = opts || {};

    lastKnownCart = cart || lastKnownCart;

    if (isExcludedPage()) {
      wrap.style.setProperty("display", "none", "important");
      wrap.setAttribute("aria-hidden", "true");
      wrap.classList.remove("custom-sticky-cart--animate-in");
      lastItemCount = 0;
      lastLineCount = 0;
      return;
    }

    if (!cart || !cart.items || !cart.items.length) {
      lastFrontKey = null;
      prevFrontKey = null;
      var isVisible =
        wrap.style.display !== "none" &&
        wrap.getAttribute("aria-hidden") !== "true";
      if (isVisible) {
        wrap.classList.remove("custom-sticky-cart--animate-in");
        wrap.classList.add("custom-sticky-cart--animate-out");
        wrap.addEventListener("animationend", function onEnd() {
          wrap.removeEventListener("animationend", onEnd);
          wrap.style.setProperty("display", "none", "important");
          wrap.setAttribute("aria-hidden", "true");
          wrap.classList.remove(
            "custom-sticky-cart--animate-out",
            "custom-sticky-cart--expanded",
            "custom-sticky-cart--compact",
            "custom-sticky-cart--reveal",
          );
        });
      } else {
        wrap.style.setProperty("display", "none", "important");
        wrap.setAttribute("aria-hidden", "true");
      }
      lastItemCount = 0;
      lastLineCount = 0;
      return;
    }

    // Newest (last-added) always first so it keeps z-index / drop-in on top
    const items = getDisplayItems(cart);
    const plural = cart.item_count === 1 ? "" : "s";
    const didAdd = lastItemCount >= 0 && cart.item_count > lastItemCount;
    // Pop-out only when a product line is fully removed (qty → 0), not 3 → 2.
    // Require fewer cart lines — qty-only updates keep the same items.length.
    const didRemove =
      lastItemCount > 0 &&
      cart.item_count < lastItemCount &&
      cart.item_count > 0 &&
      cart.items.length < lastLineCount &&
      !!opts.lineRemoved;
    const wasEmpty = lastItemCount === 0 || lastItemCount < 0;
    const isFirstItem = didAdd && wasEmpty;

    if (didRemove) {
      var firstImgWrap = imgWrap.querySelector(
        ".custom-sticky-cart__img-wrap:first-child",
      );
      if (firstImgWrap) {
        var rect = firstImgWrap.getBoundingClientRect();
        var imgCenterX = rect.left + rect.width / 2;
        var disappearY = rect.top - 80;

        firstImgWrap.classList.add("custom-sticky-cart__img-wrap--pop-out");

        setTimeout(function () {
          var gifOverlay = document.createElement("span");
          gifOverlay.className = "custom-sticky-cart__gif-overlay";
          gifOverlay.style.left = imgCenterX - 30 + "px";
          gifOverlay.style.top = disappearY - 30 + "px";
          var gifImg = document.createElement("img");
          gifImg.src =
            "https://cdn.shopify.com/s/files/1/0888/8057/8807/files/50x50.gif?v=1770967574";
          gifImg.alt = "";
          gifOverlay.appendChild(gifImg);
          document.body.appendChild(gifOverlay);

          setTimeout(function () {
            if (gifOverlay.parentNode)
              gifOverlay.parentNode.removeChild(gifOverlay);
          }, 700);
        }, 350);

        setTimeout(function () {
          rebuildImages(imgWrap, items, false, false);
          lastItemCount = cart.item_count;
          lastLineCount = cart.items.length;
          cnt.textContent = cart.item_count + " Item" + plural;
          updatePosition();
        }, 1000);
        return;
      }
    }

    rebuildImages(imgWrap, items, didAdd, isFirstItem);

    lastItemCount = cart.item_count;
    lastLineCount = cart.items.length;

    cnt.textContent = `${cart.item_count} Item${plural}`;
    const wasHidden =
      wrap.style.display === "none" ||
      wrap.getAttribute("aria-hidden") === "true";

    if (wasHidden) {
      wrap.classList.remove(
        "custom-sticky-cart--animate-in",
        "custom-sticky-cart--expanded",
        "custom-sticky-cart--compact",
        "custom-sticky-cart--reveal",
      );
      if (isFirstItem) {
        wrap.classList.add("custom-sticky-cart--compact");
        wrap.offsetHeight;
      } else {
        wrap.classList.add(
          "custom-sticky-cart--expanded",
          "custom-sticky-cart--reveal",
        );
      }
      wrap.style.removeProperty("display");
      wrap.setAttribute("aria-hidden", "false");
      wrap.offsetHeight;
      wrap.classList.add("custom-sticky-cart--animate-in");
      updatePosition();
      // Retry after a short delay to catch progress bar that appears with the cart add
      scheduleDelayedPositionUpdate(150);
      if (isFirstItem) {
        setTimeout(function () {
          wrap.classList.remove("custom-sticky-cart--compact");
          wrap.classList.add("custom-sticky-cart--expanded");
          setTimeout(function () {
            wrap.classList.add("custom-sticky-cart--reveal");
            updatePosition();
            // Final position check after expansion completes
            scheduleDelayedPositionUpdate(50);
          }, 350);
        }, 500);
      }
    } else if (isFirstItem) {
      wrap.classList.remove(
        "custom-sticky-cart--expanded",
        "custom-sticky-cart--reveal",
      );
      wrap.classList.add("custom-sticky-cart--compact");
      wrap.offsetHeight;
      setTimeout(function () {
        wrap.classList.remove("custom-sticky-cart--compact");
        wrap.classList.add("custom-sticky-cart--expanded");
        setTimeout(function () {
          wrap.classList.add("custom-sticky-cart--reveal");
          updatePosition();
          // Final position check after expansion completes
          scheduleDelayedPositionUpdate(50);
        }, 350);
      }, 500);
    } else {
      wrap.classList.remove("custom-sticky-cart--compact");
      wrap.classList.add(
        "custom-sticky-cart--expanded",
        "custom-sticky-cart--reveal",
      );
      updatePosition();
      // Retry after a short delay to catch progress bar changes
      scheduleDelayedPositionUpdate(150);
    }
  }

  function applyCart(cart, opts) {
    if (!cart) return;
    opts = opts || {};
    var prevCart = lastKnownCart;
    var lineRemoved = hasFullyRemovedLine(prevCart, cart);
    if (opts.detectFront !== false) {
      var bumped = detectFrontKeyFromCartDelta(prevCart, cart);
      if (bumped) rememberFrontKey(bumped);
    }
    var sig = cartSignature(cart) + "|f:" + (lastFrontKey || "");
    // Skip identical re-renders (optimistic → cart:change with same lines)
    if (sig === lastSignature && lastItemCount >= 0) {
      lastKnownCart = cart;
      return;
    }
    lastSignature = sig;
    lastKnownCart = cart;
    render(cart, { lineRemoved: lineRemoved });
  }

  function openShopifyCartDrawer() {
    const cartDrawer = document.querySelector("hdt-cart-drawer");
    if (cartDrawer && typeof cartDrawer.openDialog === "function") {
      cartDrawer.openDialog();
      return;
    }
    if (cartDrawer) {
      cartDrawer.setAttribute("open", "");
      return;
    }
    const cartIcon = document.querySelector('[aria-controls="CartDrawer"]');
    if (cartIcon) {
      cartIcon.click();
      return;
    }
    window.location.href = "/cart";
  }

  function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (typeof window.fireViewCart === "function") {
      window.fireViewCart("sticky_cart");
    }
    openShopifyCartDrawer();
  }

  function bind() {
    const btn = trigger();
    if (btn) {
      btn.removeEventListener("click", handleClick);
      btn.addEventListener("click", handleClick);
    }
  }

  function onCartEvent(ev) {
    var cart = ev.detail && ev.detail.cart;
    if (cart && Array.isArray(cart.items)) {
      applyCart(cart);
      return;
    }
    // Rare: refresh without payload — fetch immediately (no 300ms lag)
    fetchCart().then(applyCart);
  }

  function init() {
    bind();
    // Initial hydrate; ignore stale empty cart.js if ATC already painted the pill
    fetchCart().then(function (cart) {
      if (
        lastKnownCart &&
        (lastKnownCart.item_count || 0) > 0 &&
        (!cart || !(cart.item_count || 0))
      ) {
        return;
      }
      applyCart(cart);
    });
    updatePosition();

    document.addEventListener("cart:change", onCartEvent);
    document.addEventListener("cart:refresh", onCartEvent);

    // Re-stack on scroll / chrome show-hide (same triggers as progress-bar offset)
    window.addEventListener("scroll", schedulePositionUpdate, { passive: true });
    window.addEventListener("resize", schedulePositionUpdate, { passive: true });
    window.addEventListener("orientationchange", schedulePositionUpdate, {
      passive: true,
    });
    window.addEventListener("load", schedulePositionUpdate, { once: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", schedulePositionUpdate, {
        passive: true,
      });
      window.visualViewport.addEventListener("scroll", schedulePositionUpdate, {
        passive: true,
      });
    }

    // Keep pill stacked above the free-delivery strip when that bar moves
    if (typeof MutationObserver !== "undefined") {
      const progress = document.querySelector(".cart-progressBar");
      if (progress) {
        // Watch for style, class, aria-hidden changes on the progress bar
        new MutationObserver(function () {
          schedulePositionUpdate();
          // Also schedule a delayed check in case the bar is mid-animation
          scheduleDelayedPositionUpdate(100);
        }).observe(progress, {
          attributes: true,
          attributeFilter: ["style", "class", "aria-hidden"],
        });

        // Watch for child changes (message content updates that affect height)
        new MutationObserver(schedulePositionUpdate).observe(progress, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
      new MutationObserver(schedulePositionUpdate).observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });
      // Progress bar bottom is driven by --hdt-cart-progress-bottom on <html>
      new MutationObserver(schedulePositionUpdate).observe(
        document.documentElement,
        { attributes: true, attributeFilter: ["style"] },
      );
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
